/**
 * Lab Report Service
 * Handles lab report upload, processing, review, and matching API calls
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { LAB_REPORT_ENDPOINTS, buildQueryString } from '../endpoints';

export const LabReportStatus = {
  QUEUED: 'Queued',
  PROCESSING: 'Processing',
  NEEDS_REVIEW: 'NeedsReview',
  READY: 'Ready',
  FAILED: 'Failed',
};

export const getLabReportStatusLabel = (status) => {
  const labels = {
    Queued: 'Queued',
    Processing: 'Processing',
    NeedsReview: 'Needs Review',
    Ready: 'Ready',
    Failed: 'Failed',
  };
  return labels[status] || status;
};

export const LabSampleType = {
  FIELD: 'Field',
  QC_BLANK: 'QC_Blank',
  QC_SPIKE: 'QC_Spike',
  QC_DUPLICATE: 'QC_Duplicate',
  QC_STANDARD_REFERENCE: 'QC_StandardReference',
  UNKNOWN: 'Unknown',
};

export const getLabSampleTypeLabel = (type) => {
  const labels = {
    Field: 'Field',
    QC_Blank: 'QC Blank',
    QC_Spike: 'QC Spike',
    QC_Duplicate: 'QC Duplicate',
    QC_StandardReference: 'QC Standard Reference',
    Unknown: 'Unknown',
  };
  return labels[type] || type;
};

const labReportService = {
  getLabReports: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${LAB_REPORT_ENDPOINTS.LIST}${queryString}`)
    );
  },

  getLabReportById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.GET_BY_ID(id))
    );
  },

  getRawData: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.RAW_DATA(id))
    );
  },

  getUploadUrl: async (fileName, fileSizeBytes) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.UPLOAD_URL, {
        params: { fileName, fileSizeBytes },
      })
    );
  },

  uploadToS3: (presignedUrl, file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      const contentType = file.name.endsWith('.xlsx')
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      xhr.setRequestHeader('Content-Type', contentType);

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
      xhr.ontimeout = () => reject(new Error('S3 upload timed out'));
      xhr.timeout = 300000; // 5 minutes

      xhr.send(file);
    });
  },

  confirmUpload: async (data) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.CONFIRM, data)
    );
  },

  getPdfUrl: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.PDF_URL(id))
    );
  },

  approveLabReport: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.APPROVE(id))
    );
  },

  updateSample: async (reportId, sampleId, data) => {
    return apiRequest(() =>
      axiosInstance.put(LAB_REPORT_ENDPOINTS.UPDATE_SAMPLE(reportId, sampleId), data)
    );
  },

  updateResult: async (reportId, resultId, data) => {
    return apiRequest(() =>
      axiosInstance.put(LAB_REPORT_ENDPOINTS.UPDATE_RESULT(reportId, resultId), data)
    );
  },

  addResult: async (reportId, data) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.ADD_RESULT(reportId), data)
    );
  },

  deleteResult: async (reportId, resultId) => {
    return apiRequest(() =>
      axiosInstance.delete(LAB_REPORT_ENDPOINTS.DELETE_RESULT(reportId, resultId))
    );
  },

  matchWorkOrder: async (reportId, data) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.MATCH_WORK_ORDER(reportId), data)
    );
  },

  getSuggestedMatches: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.SUGGEST_MATCHES(reportId))
    );
  },

  deleteLabReport: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(LAB_REPORT_ENDPOINTS.DELETE(id))
    );
  },

  attemptMatch: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.ATTEMPT_MATCH(id))
    );
  },

  linkSubmission: async (id, submissionId, reason) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.LINK_SUBMISSION(id, submissionId), { reason: reason || null })
    );
  },

  getSubmissionCandidates: async (id, query) => {
    const params = query ? { query } : {};
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.SUBMISSION_CANDIDATES(id), { params })
    );
  },

  unlinkSubmission: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(LAB_REPORT_ENDPOINTS.UNLINK_SUBMISSION(id))
    );
  },

  getLinkedSubmission: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.LINKED_SUBMISSION(id))
    );
  },

  // Sample-level linking
  linkSampleSubmission: async (reportId, sampleId, submissionId, reason) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.SAMPLE_LINK_SUBMISSION(reportId, sampleId, submissionId), { reason: reason || null })
    );
  },

  unlinkSampleSubmission: async (reportId, sampleId) => {
    return apiRequest(() =>
      axiosInstance.delete(LAB_REPORT_ENDPOINTS.SAMPLE_UNLINK_SUBMISSION(reportId, sampleId))
    );
  },

  getSampleCandidates: async (reportId, sampleId, query) => {
    const params = query ? { query } : {};
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.SAMPLE_CANDIDATES(reportId, sampleId), { params })
    );
  },

  getSampleLinks: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.SAMPLE_LINKS(reportId))
    );
  },

  // Submission-side linking
  getLabSamplesForSubmission: async (submissionId) => {
    return apiRequest(() =>
      axiosInstance.get(LAB_REPORT_ENDPOINTS.SUBMISSION_SAMPLE_LINKS(submissionId))
    );
  },

  attemptMatchForSubmission: async (submissionId) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.SUBMISSION_ATTEMPT_MATCH(submissionId))
    );
  },

  evaluate: async (data) => {
    return apiRequest(() =>
      axiosInstance.post(LAB_REPORT_ENDPOINTS.EVALUATE, data, {
        timeout: 600000, // 10 minutes — large documents: Textract + parallel AI passes
      })
    );
  },
};

export default labReportService;
